import { FC, useCallback, useMemo, useRef } from 'react';
import {
    DndContext,
    DragEndEvent,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext } from '@dnd-kit/sortable';

import { Box, Chip, GridColDef } from '@thirdspacelearning/library';

import DraggableGridRow from './DraggableGridRow';
import DragHandleIcon from './DragHandleIcon';

import { CMSDataGrid } from '@shared';
import {
    Country,
    GBLearningObjective,
    LearningObjective,
    USLearningObjective,
} from '@types';
import { yearGroupSortComparator } from '../../../utils';

type Props = {
    sortedLOs: LearningObjective[];
    setSortedLOs: (sortedLOs: LearningObjective[]) => void;
    country: Country;
};

const sx = {
    loTitleCell: {
        whiteSpace: 'normal',
    },
    aiChip: {
        mb: '2px',
        mr: 1,
    },
};

const ProgrammeLoReorderTable: FC<Props> = ({
    sortedLOs,
    setSortedLOs,
    country,
}) => {
    const sensors = useSensors(useSensor(PointerSensor));

    const sortedLOsRef = useRef(sortedLOs);
    sortedLOsRef.current = sortedLOs;

    const gbColumns = useMemo(
        (): GridColDef<LearningObjective>[] =>
            [
                {
                    field: 'yearGroup',
                    type: 'string',
                    headerName: 'Year group',
                    headerAlign: 'center',
                    align: 'center',
                    sortingOrder: ['asc', 'desc'],
                    sortComparator: yearGroupSortComparator,
                    renderCell: ({ row }) =>
                        row.year_id ? (
                            <Chip
                                label={`Year ${(row as GBLearningObjective).year_id}`}
                                variant="outlined"
                                size="small"
                            />
                        ) : (
                            '-'
                        ),
                    sortable: false,
                    minWidth: 110,
                },
            ] as GridColDef<LearningObjective>[],
        [],
    );

    const usColumns = useMemo(
        (): GridColDef<LearningObjective>[] =>
            [
                {
                    field: 'yearGroup',
                    type: 'string',
                    headerName: 'Grade',
                    headerAlign: 'center',
                    align: 'center',
                    sortingOrder: ['asc', 'desc'],
                    sortComparator: yearGroupSortComparator,
                    renderCell: ({ row }) =>
                        row.year_id ? (
                            <Chip
                                label={`Grade ${(row as USLearningObjective).year_id}`}
                                variant="outlined"
                                size="small"
                            />
                        ) : (
                            '-'
                        ),
                    sortable: false,
                    minWidth: 110,
                },
            ] as GridColDef<LearningObjective>[],
        [],
    );

    const columns = useMemo(
        (): GridColDef<LearningObjective>[] => [
            {
                field: 'icon',
                headerName: '',
                headerAlign: 'center',
                align: 'center',
                sortable: false,
                minWidth: 40,
                maxWidth: 40,
                renderCell: () => <DragHandleIcon />,
            },
            {
                field: 'title',
                type: 'string',
                headerName: 'Learning objective',
                renderCell: ({ row }) => (
                    <Box sx={sx.loTitleCell}>
                        {row.ai && (
                            <Chip
                                label={'AI'}
                                color="info"
                                size="small"
                                sx={sx.aiChip}
                            />
                        )}
                        {row.title}
                    </Box>
                ),
                sortable: false,
                minWidth: 400,
                flex: 1,
            },
            ...(country === Country.GB ? gbColumns : usColumns),
            {
                field: 'id',
                type: 'string',
                headerName: 'ID',
                headerAlign: 'center',
                align: 'center',
                sortable: false,
            },
        ],
        [country, gbColumns, usColumns],
    );

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;
            if (!over) {
                return;
            }

            const currentLOs = sortedLOsRef.current;
            const oldIndex = currentLOs.findIndex(
                (los) => los.id === active.id,
            );
            const newIndex = currentLOs.findIndex((los) => los.id === over.id);

            if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
                return;
            }

            setSortedLOs(arrayMove(currentLOs, oldIndex, newIndex));
        },
        [setSortedLOs],
    );

    const loIds = useMemo(() => sortedLOs.map((lo) => lo.id), [sortedLOs]);

    return (
        sortedLOs && (
            <DndContext
                sensors={sensors}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToVerticalAxis]}
            >
                <SortableContext items={loIds}>
                    <CMSDataGrid
                        columns={columns}
                        rows={sortedLOs}
                        disableColumnMenu
                        disableColumnSelector
                        disableColumnFilter
                        disableDensitySelector
                        disableRowSelectionOnClick
                        disableColumnResize
                        hideFooter
                        slots={{ row: DraggableGridRow }}
                    />
                </SortableContext>
            </DndContext>
        )
    );
};

export default ProgrammeLoReorderTable;
